import './LeaderboardDetails.sass';

import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRoute } from 'react-router5';

import {
  Button,
  Header,
  InfoBlock,
  List,
  ListItem,
  Placeholder,
  ScrollView,
  Tabs,
  TabsItem,
} from '$uikit';
import { Loader, UnitName } from '$shared/components';
import { useCurrentUserInfo, useTranslator, useUserInfo } from '$hooks';
import { leaderboardActions, selectLeaderboard } from '$store/leaderboard';
import { format, formatCurrency } from '$utils';
import { PROFILE } from '$shared/constants/pages';
import router from '$navigation/router';

type TabType = 'transactions' | 'users';

const NewUserItem: FC<{ userId: number; points: number }> = ({ userId, points }) => {
  const user = useUserInfo(userId);
  if (!user) return null;

  const handleClick = () => {
    router.navigate(PROFILE, { id: userId });
  };

  return (
    <ListItem onClick={handleClick} chevron>
      <div className="LeaderboardDetails__user-row">
        <UnitName unit={user} />
        <span className="LeaderboardDetails__user-points">+{points}</span>
      </div>
    </ListItem>
  );
};

export const LeaderboardDetails: FC = () => {
  const t = useTranslator();
  const dispatch = useDispatch();
  const { route } = useRoute();
  const leaderboardId = parseInt(route.params.id);

  const curUser = useCurrentUserInfo();
  const isModel = curUser && curUser.verified === 2;
  const { type, details, detailsLoadingStatus } = useSelector(selectLeaderboard);

  const [activeTab, setActiveTab] = useState<TabType>('transactions');

  const handleLoad = () => {
    dispatch(leaderboardActions.loadDetails(leaderboardId));
  };

  useEffect(() => {
    if (leaderboardId) {
      handleLoad();
    }
  }, [leaderboardId]);

  if (!isModel) {
    return (
      <>
        <Header showBackButton>{t('leaderboard_details')}</Header>
        <ScrollView>
          <Placeholder centered title={t('access_denied')}>
            {t('leaderboard_details_model_only')}
          </Placeholder>
        </ScrollView>
      </>
    );
  }

  const renderContent = () => {
    if (detailsLoadingStatus === 'loading' && !details) {
      return <Loader isWrapped />;
    }

    if (detailsLoadingStatus === 'failed') {
      return (
        <Placeholder
          title={t('failed_title')}
          centered
          actions={<Button onClick={handleLoad}>{t('failed_button')}</Button>}
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    if (!details) {
      return null;
    }

    return (
      <div className="LeaderboardDetails__container">
        <div className="LeaderboardDetails__header">
          <div className="LeaderboardDetails__header-item">
            <div className="LeaderboardDetails__header-label">
              {t('leaderboard_total_points')}
            </div>
            <div className="LeaderboardDetails__header-value">{details.totalPoints}</div>
          </div>
          <div className="LeaderboardDetails__header-item">
            <div className="LeaderboardDetails__header-label">
              {t('leaderboard_rank')}
            </div>
            <div className="LeaderboardDetails__header-value">#{details.rank + 1}</div>
          </div>
        </div>

        {type === 'happy_new_year_2026' && (
          <div className="LeaderboardDetails__points-breakdown">
            <InfoBlock type="info">
              <div className="LeaderboardDetails__points-item">
                <span>{t('leaderboard_payment_points')}:</span>
                <strong>{details.paymentPoints || 0}</strong>
              </div>
              <div className="LeaderboardDetails__points-item">
                <span>{t('leaderboard_new_user_points')}:</span>
                <strong>{details.newUserPoints || 0}</strong>
              </div>
            </InfoBlock>
          </div>
        )}

        {type === 'happy_new_year_2026' && details.newPayingUsers ? (
          <>
            <Tabs selected={activeTab} onChange={setActiveTab}>
              <TabsItem value="transactions">{t('leaderboard_transactions')}</TabsItem>
              <TabsItem value="users">{t('leaderboard_new_users')}</TabsItem>
            </Tabs>
            {activeTab === 'transactions' ? renderTransactions() : renderNewUsers()}
          </>
        ) : (
          renderTransactions()
        )}
      </div>
    );
  };

  const renderTransactions = () => {
    if (!details?.transactions || details.transactions.length === 0) {
      return <Placeholder centered>{t('leaderboard_no_transactions')}</Placeholder>;
    }

    return (
      <div className="LeaderboardDetails__table-container">
        <table className="LeaderboardDetails__table">
          <thead>
            <tr>
              <th>{t('leaderboard_table_operation')}</th>
              <th>{t('leaderboard_table_amount')}</th>
              <th>{t('leaderboard_table_date')}</th>
              <th>ID</th>
            </tr>
          </thead>
          <tbody>
            {details.transactions.map((transaction) => {
              const date = new Date(transaction.createdAt * 1000);
              const formattedDate = format(date, 'dd MMM yyyy');
              const formattedTime = format(date, 'HH:mm');

              return (
                <tr key={transaction.id}>
                  <td className="LeaderboardDetails__table-operation">
                    {t(`balance_operation_${transaction.operation}_title`)}
                  </td>
                  <td className="LeaderboardDetails__table-amount">
                    {formatCurrency(transaction.amount, transaction.currency)}
                  </td>
                  <td className="LeaderboardDetails__table-date">
                    <div>{formattedDate}</div>
                    <div className="LeaderboardDetails__table-time">{formattedTime}</div>
                  </td>
                  <td className="LeaderboardDetails__table-id">{transaction.id}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderNewUsers = () => {
    if (!details?.newPayingUsers || details.newPayingUsers.length === 0) {
      return <Placeholder centered>{t('leaderboard_no_new_users')}</Placeholder>;
    }

    const points = type === 'happy_new_year_2026' ? 80 : 0;

    return (
      <div className="LeaderboardDetails__list-container">
        <List>
          {details.newPayingUsers.map((item) => (
            <NewUserItem key={item} userId={item} points={points} />
          ))}
        </List>
      </div>
    );
  };

  return (
    <>
      <Header showBackButton>{t('leaderboard_details')}</Header>
      <ScrollView>{renderContent()}</ScrollView>
    </>
  );
};
